
import {
  formatByCountry,
  formatDecimal,
  formatInputByCountry,
  parseByCountry
} from "@/app/infrastructures/misc/Utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { Options, prop, Vue } from "vue-class-component";
import PhotoDispute from "./photo-dispute.vue";
import { debounce } from "lodash";
import {
  CALCULATE_TYPE,
  getConfigDimensionDivider,
  getConfigIsminimumVolumePerPiece,
  getConfigMinimumVolumePerPiece
} from "../../modules/cargo-module";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import {
  CalculateVolumeApiRequestV2,
  CalculateVolumeRequestV2
} from "@/data/payload/api/CargoV2ApiRequest";
import { FlagsPermissionCargoV2 } from "@/feature-flags/flags-permission-cargo-v2";
import { formatInputNoDecimal } from "@/app/infrastructures/misc/common-library/FormatInputNoDecimal";

class Props {
  isEdit = prop<boolean>({
    type: Boolean,
    default: false
  });
}

@Options({
  components: {
    PhotoDispute
  },
  watch: {
    airportRouteTypeWatchKey: {
      handler() {
        CargoV2Controller.refreshAirportRouteType();
      }
    }
  }
})
class CargoDisputeForm extends Vue.with(Props) {
  CALCULATE_TYPE = CALCULATE_TYPE;

  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }

  mounted() {
    if (this.isEdit) return;
    CargoV2Controller.refreshAirportRouteType();
  }

  // GENESIS-24172: re-fetch Horde route-type whenever cargo type, origin, or
  // destination city changes (manual/RTC/edit/rebooking all render this form).
  get airportRouteTypeWatchKey() {
    return `${this.form.cargoType.value}|${this.form.originCityCode}|${this.form
      .destinationCityCode.code || ""}`;
  }

  get divider() {
    return getConfigDimensionDivider(this.form.cargoType.value);
  }

  get maxGrossText() {
    return formatDecimal(
      CargoV2Controller.planeBookingLimits.maxGross.toString()
    );
  }

  get maxVolumeText() {
    return formatDecimal(
      CargoV2Controller.planeBookingLimits.maxVolume.toString()
    );
  }

  get maxPiecesText() {
    return formatDecimal(
      CargoV2Controller.planeBookingLimits.maxPieces.toString()
    );
  }

  get loading() {
    return CargoV2Controller.isLoadingCalculateVolume;
  }

  get isEditDimension() {
    return CargoV2Controller.isDimensionChanged;
  }

  get isUploadProofPhoto() {
    return CargoV2Controller.isUploadProofPhoto;
  }

  isEditFirstLanding = true;
  get errorDisputeCargo() {
    const defaultValue = { message: "", tooltip: "" };

    if (this.isEditFirstLanding && this.isEdit) return defaultValue;

    if (CargoV2Controller.isBagOnlyExceedMaxDisputeWeight) {
      return {
        message: this.$t("cargoV2.components.cargoDisputeForm.error.baggingWeightLocked"),
        tooltip: this.$t("cargoV2.components.cargoDisputeForm.error.baggingWeightLockedTooltip")
      };
    }

    if (CargoV2Controller.isExceedMaxDisputeWeight) {
      return {
        message: this.$t("cargoV2.components.cargoDisputeForm.error.maxCargoChargeableWeightDiff", {
          maxWeight: FlagsPermissionCargoV2.config_max_dispute_weight.getValue()
        }),
        tooltip: ""
      };
    }

    if (this.isUploadProofPhoto) {
      return {
        message: this.$t("cargoV2.components.cargoDisputeForm.error.chargeableWeightDifference"),
        tooltip: ""
      };
    }

    return defaultValue;
  }

  get form() {
    return CargoV2Controller.form;
  }

  get isGrossWeightInputError() {
    return CargoV2Controller.isGrossWeightInputError;
  }

  get isPiecesInputError() {
    return CargoV2Controller.isPiecesInputError;
  }

  get isVolumeWeightError() {
    return CargoV2Controller.isVolumeWeightError;
  }

  get totalReweighChargeableWeight() {
    return this.form.cargoTotalEstimateChargeableWeight;
  }

  get totalReweighGrossWeights() {
    return this.form.totalAccumulationEstimationGrossWeightDataTable;
  }

  get totalReweighVolumeWeights() {
    return this.form.totalAccumulationEstimationVolumeWeightDataTable;
  }

  get totalReweightPieces() {
    return this.form.totalAccumulationActualPieceDataTable;
  }

  get totalReweightBag() {
    return CargoV2Controller.totalBagDataTable;
  }

  get totalReweightStt() {
    return CargoV2Controller.totalSttDataTable;
  }

  get cargoTypeLabel() {
    if (!this.form.cargoType.value) return "";
    const obj = {
      plane: this.$t("cargoV2.components.cargoDisputeForm.cargoType.plane"),
      truck: this.$t("cargoV2.components.cargoDisputeForm.cargoType.truck"),
      ship: this.$t("cargoV2.components.cargoDisputeForm.cargoType.ship")
    } as any;

    return obj[this.form.cargoType.value] ?? "";
  }

  get actualTotalCargoGrossweigth() {
    return this.formatFloat(this.form.actualTotalCargoGrossweigth as any);
  }

  set actualTotalCargoGrossweigth(value: any) {
    this.form.actualTotalCargoGrossweigth =
      typeof value === "string"
        ? parseByCountry(value, { country: this.countryCode })
        : Number(value);
  }

  get cargoActualPiece() {
    return this.formatNoDecimal(this.form.cargoActualPiece as any);
  }

  set cargoActualPiece(value: any) {
    this.form.cargoActualPiece =
      typeof value === "string"
        ? parseByCountry(value, { country: this.countryCode })
        : Number(value);
  }

  async onChangeDimension() {
    await this.$nextTick();
    this.isEditFirstLanding = false;

    this.emitCalculate(this.CALCULATE_TYPE.CHANGE_DIMENSION);

    // called for update to session storage
    CargoV2Controller.setForm(this.form);
    CargoV2Controller.setIsDimensionChanged(true);
  }

  async onChangePiece() {
    await this.$nextTick();
    this.isEditFirstLanding = false;

    this.emitCalculate(this.CALCULATE_TYPE.CHANGE_PCS);

    // called for update to session storage
    this.form.cargoActualPiece =
      typeof this.form.cargoActualPiece === "string"
        ? parseByCountry(this.form.cargoActualPiece, { country: this.countryCode })
        : Number(this.form.cargoActualPiece);
    CargoV2Controller.setForm(this.form);
  }

  async onChangeGrossWeight() {
    this.isEditFirstLanding = false;
    await this.$nextTick();
    this.emitCalculate(this.CALCULATE_TYPE.CHANGE_GW);

    // called for update to session storage
    this.form.actualTotalCargoGrossweigth =
      typeof this.form.actualTotalCargoGrossweigth === "string"
        ? parseByCountry(this.form.actualTotalCargoGrossweigth, { country: this.countryCode })
        : Number(this.form.actualTotalCargoGrossweigth);
    CargoV2Controller.setForm(this.form);
  }

  emitCalculate = debounce((type: string, _this = this) => {
    _this.calculateVolume(type);
  }, 400).bind(this);

  async calculateVolume(type: string) {
    this.form.calculateType = type;
    const minVolumePerPiece = getConfigMinimumVolumePerPiece(
      this.form.cargoType.value,
      "auto"
    );
    const isMinimumVolumePerPiece = getConfigIsminimumVolumePerPiece(
      this.form.cargoType.value,
      "auto"
    );
    CargoV2Controller.calculateVolume(
      new CalculateVolumeApiRequestV2({
        calculateType: type,
        calculateRequest: new CalculateVolumeRequestV2({
          cargoType: this.form.cargoType.value,
          length: this.form.actualDimension.cargoActualLengthDimension,
          width: this.form.actualDimension.cargoActualWidthDimension,
          height: this.form.actualDimension.cargoActualHeightDimension,
          chargeableWeightReweigh: this.totalReweighChargeableWeight,
          currentGrossWeight: this.form.actualTotalCargoGrossweigth,
          divider: this.divider,
          isMinimumVolumePerPiece,
          minVolumePerPiece,
          totalPieces: this.form.cargoActualPiece,
          volumeWeightReweigh: this.totalReweighVolumeWeights
        })
      })
    );
  }

  formatFloat(value: string) {
    return formatInputByCountry(value, { type: "weight", country: this.countryCode });
  }

  formatNoDecimal(value: string) {
    return formatInputByCountry(value, { type: "quantity", country: this.countryCode });
  }

  formatDimension(value: string) {
    return formatInputNoDecimal(value);
  }

  formatGrossWeight() {
    const grossWeight = Math.max(
      0.1,
      typeof this.form.actualTotalCargoGrossweigth === "string"
        ? parseByCountry(this.form.actualTotalCargoGrossweigth as string, { country: this.countryCode })
        : this.form.actualTotalCargoGrossweigth
    );
    CargoV2Controller.setForm({
      actualTotalCargoGrossweigth: grossWeight
    });
    this.calculateVolume(CALCULATE_TYPE.CHANGE_GW);
  }

  convertDecimal(value: string | number) {
    const num = typeof value === "string" ? parseByCountry(value, { country: this.countryCode }) : Number(value);
    return formatByCountry(num, { type: "weight", country: this.countryCode });
  }
}
export default CargoDisputeForm;
